/**
 * @module ApplicationContext/update
 * @description Functions to update reactive variables in the application context
 */

import { cookies as cookieUtilities, signifyd as signifydUtilities } from '@xp-utilities/utilities';
import {
  applicationContextVar,
  countryVar,
  placementsVar,
  signifydIdVar,
  userTypeVar,
  xSegVar,
} from './ApplicationContext';

/**
 * @function updateApplicationContextVar
 * @description Updates the application context reactive variable with filtered query parameters
 * @param {Object} ctx - The context object containing query parameters
 * @param {Object} [ctx.query] - Query parameters from the request
 */
export const updateApplicationContextVar = (ctx) => {
  const { query } = ctx;

  if (!query) {
    applicationContextVar(null);
    return;
  }

  const includeList = ['brand', 'catalogId', 'country', 'langId', 'store', 'storeId'];

  const filteredQueryObj = Object.keys(query)
    .filter((key) => includeList.includes(key))
    .reduce((obj, key) => {
      const object = obj;
      object[key] = query[key];
      return object;
    }, {});

  applicationContextVar(JSON.stringify(filteredQueryObj));
};

/**
 * @function updatePlacementsVar
 * @description Updates the placements reactive variable for a specific ID
 * @param {Object} ctx - The context object containing query parameters
 * @param {Object} [ctx.query={}] - Query parameters from the request
 * @param {string} [ctx.query.placements] - Pipe-separated placements string
 * @param {string} id - Identifier for the placements
 */
export const updatePlacementsVar = (ctx, id) => {
  const { query = {} } = ctx;
  const { placements } = query;

  if (!id) return;
  if (!placements) return;

  const parsedPlacements = placements.split('|');
  placementsVar().set(id, parsedPlacements);
};

/**
 * @function updateXSegVar
 * @description Updates the xSeg reactive variable from cookies
 * @param {Object} ctx - The context object containing cookies
 * @param {Object} [ctx.cookies={}] - Cookies from the request
 * @param {string} [ctx.cookies.xSeg] - xSeg cookie value
 */
export const updateXSegVar = (ctx) => {
  const { cookies = {} } = ctx;
  xSegVar(cookies.xSeg || null);
};

/**
 * @function updateUserTypeVar
 * @description Updates the user type reactive variable using cookie utilities
 * @param {Object} ctx - The context object to pass to getUserType
 */
export const updateUserTypeVar = (ctx) => {
  const { getUserType } = cookieUtilities;
  userTypeVar(getUserType(ctx));
};

/**
 * @function updateCountryVar
 * @description Updates the country reactive variable from query parameters
 * @param {Object} ctx - The context object containing query parameters
 * @param {Object} [ctx.query={}] - Query parameters from the request
 * @param {string} [ctx.query.country] - Country code from query parameters
 */
export const updateCountryVar = (ctx) => {
  const { query = {} } = ctx;
  countryVar(query.country);
};

/**
 * @function updateSignifydIdVar
 * @description Updates the Signifyd ID reactive variable from the document
 * @returns {void}
 */
export const updateSignifydIdVar = () => {
  if (!document) return;

  const { getSignifydSessionID } = signifydUtilities;
  const signifydSessionID = getSignifydSessionID(document);
  signifydIdVar(signifydSessionID);
};
